import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Search, Loader2, Users, Building2, Tag, RefreshCw, Trash2,
  Mail, Phone, MapPin, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { particulier: "Particulier", pro: "Pro" };
const emptyNew = { type: "particulier", first_name: "", last_name: "", email: "", phone: "", company_name: "" };

function fmtCents(c) {
  const n = (c || 0) / 100;
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function ClientsAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState(emptyNew);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (typeFilter) params.set("type", typeFilter);
      if (tagFilter) params.set("tag", tagFilter);
      const r = await api.get(`/jardins-ole/admin/crm/clients?${params.toString()}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error("Erreur de chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, typeFilter, tagFilter]);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((c) => (c.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  const openNewModal = () => { setDraft(emptyNew); setModalOpen(true); };
  const submitCreate = async () => {
    if (!draft.first_name.trim() && !draft.company_name.trim()) {
      return toast.error(draft.type === "pro" ? "Raison sociale ou prénom requis" : "Prénom requis");
    }
    setCreating(true);
    try {
      const payload = { ...draft, first_name: draft.first_name.trim() || draft.company_name.trim() };
      const r = await api.post("/jardins-ole/admin/crm/clients", payload);
      toast.success("Client créé");
      setModalOpen(false);
      navigate(`/admin/clients/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de création");
    } finally {
      setCreating(false);
    }
  };

  const syncFromOrders = async () => {
    if (!window.confirm("Importer/mettre à jour les fiches client depuis les commandes ? (idempotent)")) return;
    setSyncing(true);
    try {
      const r = await api.post("/jardins-ole/admin/crm/clients/sync-from-orders");
      toast.success(`Synchro OK · ${r.data.created} créé(s), ${r.data.updated} mis à jour`);
      load();
    } catch (e) {
      toast.error("Erreur synchro");
    } finally {
      setSyncing(false);
    }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Supprimer ${c.first_name} ${c.last_name} ? Les chantiers liés seront aussi supprimés.`)) return;
    try {
      await api.delete(`/jardins-ole/admin/crm/clients/${c.id}`);
      toast.success("Client supprimé");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="clients-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
            <Users className="w-6 h-6" /> Fiches clients
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Particuliers &amp; pros, avec historique d'achats et fiche chantier.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={syncFromOrders} disabled={syncing} data-testid="crm-sync-orders-btn">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Synchro commandes
          </Button>
          <Button onClick={openNewModal} data-testid="crm-create-client-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau client
          </Button>
        </div>
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par nom, email, téléphone, ville, entreprise..."
              className="pl-9"
              data-testid="crm-search-input"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white"
            data-testid="crm-type-filter"
          >
            <option value="">Tous types</option>
            <option value="particulier">Particuliers</option>
            <option value="pro">Pros</option>
          </select>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white"
            data-testid="crm-tag-filter"
          >
            <option value="">Tous tags</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-3" />
          <div className="text-[hsl(var(--muted-foreground))] mb-4">Aucun client pour le moment.</div>
          <div className="flex justify-center gap-2">
            <Button onClick={syncFromOrders} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" /> Importer depuis les commandes
            </Button>
            <Button onClick={openNewModal}>
              <Plus className="w-4 h-4 mr-2" /> Créer manuellement
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/40 text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Contact</th>
                  <th className="px-4 py-3 text-left">Ville</th>
                  <th className="px-4 py-3 text-left">Tags</th>
                  <th className="px-4 py-3 text-right w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-[hsl(var(--muted))]/20" data-testid={`crm-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/admin/clients/${c.id}`} className="font-medium text-[color:var(--brand-navy)] hover:underline">
                        {c.type === "pro" && c.company_name
                          ? c.company_name
                          : `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
                      </Link>
                      {c.type === "pro" && c.company_name && (c.first_name || c.last_name) && (
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                          {`${c.first_name || ""} ${c.last_name || ""}`.trim()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.type === "pro" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                      }`}>
                        {c.type === "pro" ? <Building2 className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                        {TYPE_LABEL[c.type] || c.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[hsl(var(--muted-foreground))]">
                      {c.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">{c.address_city || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[hsl(var(--muted))] rounded text-[10px]">
                            <Tag className="w-2.5 h-2.5" />{t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/clients/${c.id}`}>
                        <Button size="sm" variant="ghost" data-testid={`crm-view-${c.id}`}>
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => removeClient(c)} data-testid={`crm-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="new-client-modal">
          <DialogHeader>
            <DialogTitle>Nouveau client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Type</Label>
              <div className="flex gap-2 mt-1">
                {["particulier", "pro"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setDraft({ ...draft, type: t })}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium ${
                      draft.type === t
                        ? "bg-[color:var(--brand-sky)] text-white border-[color:var(--brand-sky)]"
                        : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                    }`}
                    data-testid={`modal-type-${t}`}
                  >
                    {TYPE_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>
            {draft.type === "pro" && (
              <div>
                <Label>Raison sociale</Label>
                <Input value={draft.company_name} onChange={(e) => setDraft({ ...draft, company_name: e.target.value })} data-testid="modal-company-name" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Prénom{draft.type !== "pro" ? " *" : ""}</Label>
                <Input value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} data-testid="modal-first-name" autoFocus />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={draft.last_name} onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} data-testid="modal-last-name" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="modal-email" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} data-testid="modal-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={creating}>Annuler</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="modal-submit-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { fmtCents };
